import { useCallback } from 'react';
import { sdk } from '@/sdk';
import useSWR from 'swr';
import { Associate } from '@/types/entity/associate';
import { Address } from '@shared/types/account';
import { useStoreAndBusinessUnits } from '@/providers/store-and-business-units';
import { mapBusinessUnit } from '@/utils/mappers/map-business-unit';
import useAccount from '../useAccount';

const useBusinessUnits = () => {
  const { loggedIn, businessUnitKey } = useAccount();

  const { setSelectedBusinessUnit } = useStoreAndBusinessUnits();

  const { data, mutate: mutateBusinessUnits } = useSWR(
    !loggedIn ? null : '/action/business-unit/getBusinessUnits',
    () => sdk.composableCommerce.businessUnit.getBusinessUnits({ expandStores: true }),
  );

  const addBusinessUnit = useCallback(
    async (payload: {
      account: {
        accountId: string;
        email: string;
        companyName: string;
      };
      storeId: string;
    }) => {
      const response = await sdk.composableCommerce.businessUnit.createBusinessUnit({
        account: payload.account,
      });

      mutateBusinessUnits();

      return response.isError ? {} : response.data;
    },
    [mutateBusinessUnits],
  );

  const updateBusinessUnit = useCallback(
    async (payload: { name: string; contactEmail: string; businessUnitKey: string }) => {
      const response = await sdk.composableCommerce.businessUnit.updateBusinessUnit(
        {
          name: payload.name,
          contactEmail: payload.contactEmail,
        },
        { businessUnitKey: payload.businessUnitKey },
      );

      mutateBusinessUnits();

      return response.isError ? {} : response.data;
    },
    [mutateBusinessUnits],
  );

  const addAssociate = useCallback(
    async ({ email, roles, businessUnitKey }: Partial<Associate> & { businessUnitKey: string }) => {
      const response = await sdk.composableCommerce.businessUnit.addAssociate(
        { email: email as string, roleKeys: roles ?? [] },
        { businessUnitKey },
      );

      mutateBusinessUnits();

      return response.isError ? {} : response.data;
    },
    [mutateBusinessUnits],
  );

  const updateAssociate = useCallback(
    async ({ id, roles, businessUnitKey }: Partial<Associate> & { businessUnitKey: string }) => {
      const response = await sdk.composableCommerce.businessUnit.updateAssociate(
        { accountId: id as string, roleKeys: roles ?? [] },
        { businessUnitKey },
      );

      mutateBusinessUnits();

      return response.isError ? {} : response.data;
    },
    [mutateBusinessUnits],
  );

  const removeAssociate = useCallback(
    async ({ id, businessUnitKey }: Partial<Associate> & { businessUnitKey: string }) => {
      const response = await sdk.composableCommerce.businessUnit.removeAssociate(
        { accountId: id ?? '' },
        { businessUnitKey },
      );

      mutateBusinessUnits();

      return response.isError ? {} : response.data;
    },
    [mutateBusinessUnits],
  );

  const addAddress = useCallback(
    async ({ businessUnitKey, ...address }: Address & { businessUnitKey: string }) => {
      const response = await sdk.composableCommerce.businessUnit.addAddress({ address }, { businessUnitKey });

      mutateBusinessUnits();

      if (!response.isError) setSelectedBusinessUnit(mapBusinessUnit(response.data));

      return response.isError ? {} : response.data;
    },
    [mutateBusinessUnits, setSelectedBusinessUnit],
  );

  const updateAddress = useCallback(
    async ({ businessUnitKey, ...address }: Address & { businessUnitKey: string }) => {
      const response = await sdk.composableCommerce.businessUnit.updateAddress({ address }, { businessUnitKey });

      mutateBusinessUnits();

      return response.isError ? {} : response.data;
    },
    [mutateBusinessUnits],
  );

  const removeAddress = useCallback(
    async ({ businessUnitKey, addressId }: { addressId: string; businessUnitKey: string }) => {
      const response = await sdk.composableCommerce.businessUnit.removeAddress({ addressId }, { businessUnitKey });

      mutateBusinessUnits();

      return response.isError ? {} : response.data;
    },
    [mutateBusinessUnits],
  );

  let defaultBusinessUnitKey = null;

  if (businessUnitKey) {
    defaultBusinessUnitKey = businessUnitKey;
  } else {
    defaultBusinessUnitKey = typeof window !== 'undefined' ? localStorage.getItem('business-unit') : undefined;
  }
  const defaultBusinessUnit =
    data?.isError === true ? undefined : data?.data?.find((bu) => bu.key === defaultBusinessUnitKey) || data?.data?.[0];

  return {
    businessUnits: data?.isError ? [] : data?.data ?? [],
    defaultBusinessUnit,
    addBusinessUnit,
    updateBusinessUnit,
    addAssociate,
    updateAssociate,
    removeAssociate,
    addAddress,
    updateAddress,
    removeAddress,
  };
};

export default useBusinessUnits;
